<template>
    <div
        class="template-hero section-wrapper"
        :class="viewport.is"
        @vue:activated="getTemplateCourse(content.data)"
        @vue:mounted="getTemplateCourse(content.data)"
    >
        <section>
            <Chip
                variant="smoke"
                :text="{ size: 'xs', heading: false }"
            >
                <a href="/course-templates">
                    Templates
                </a>
                <template v-if="content && content.data && content.data.category && content.data.category.value">
                    > {{ content.data.category.value.data.title }}
                </template>
            </Chip>
            <div v-if="content && content.data" class="grid">
                <div class="grid-left">
                    <GradientText
                        tag="h1"
                        :size="viewport.is.mobile ? 'xl' : 'xxl'" heading
                    >
                        {{ content.data.title }}
                    </GradientText>
                </div>
                <div class="grid-right" />
            </div>
        </section>
    </div>
    <div
        class="template-embed section-wrapper"
        :class="{ tablet: viewport.width < 1060, mobile: viewport.is.mobile }"
    >
        <Txt v-if="!viewport.is.mobile">
            Preview course by clicking the nav arrows
        </Txt>
        <NuxtImg
            v-if="content && content.data && content.data.image && viewport.is.mobile"
            :src="content.data.image"
            :alt="content.data.imageAlt"
            provider="builderio"
            width="664"
            height="500"
            sizes="xl:150vw lg:150vw md:150vw sm:150vw xs:150vw"
            loading="lazy"
        />
        <iframe
            v-else-if="content && content.data && content.data.templateUrl"
            :src="content.data.templateUrl"
        />
        <Button
            v-if="!viewport.is.mobile"
            variant="create"
            size="xxs" heading
            data-placement="main"
            :status="isDuplicating ? 'waiting' : null"
            @click="cta('Use this template', 'main')"
        >
            Use this template
        </Button>
        <Button
            v-else-if="content && content.data && content.data.templateUrl"
            variant="create"
            size="xxs" heading
            :href="content.data.templateUrl"
            target="_blank"
            @click="$track.event('Template Previewed', {
                'button_placement': 'main',
                'button_cta': 'Preview template'
            })"
        >
            Preview template
        </Button>
    </div>
    <div
        class="template-article section-wrapper"
        :class="viewport.is"
    >
        <section>
            <article v-if="content && content.data">
                <Txt
                    size="l"
                    v-html="content.data.description"
                />
                <Content
                    model="course-template"
                    :content="content"
                    :api-key="apiKey"
                    :custom-components="customComponents"
                />
            </article>
            <aside v-if="viewport.is.desktop && content && content.data">
                <Txt size="s" heading>
                    Features included
                </Txt>
                <ul>
                    <li v-for="feature, index in features" :key="index">
                        <Icon name="correct" right />
                        <Txt>
                            {{ feature }}
                        </Txt>
                    </li>
                </ul>
                <Button
                    variant="create"
                    size="xxs" heading
                    data-placement="sidebar"
                    :status="isDuplicating ? 'waiting' : null"
                    @click="cta('Edit in course builder', 'sidebar')"
                >
                    Edit in course builder
                </Button>
            </aside>
        </section>
    </div>
    <div
        class="template-cta section-wrapper"
        :class="viewport.is"
    >
        <section>
            <GradientText
                v-if="viewport.is.mobile"
                :size="viewport.is.mobile ? 'xl' : 'xxl'" heading
                align="center"
            >
                Preview course template
            </GradientText>
            <GradientText
                v-else
                :size="viewport.is.mobile ? 'xl' : 'xxl'" heading
                align="center"
            >
                Edit this template <br />
                in our course builder
            </GradientText>
            <Txt size="l">
                Trusted & Loved by over 2,000 L&D Legends
            </Txt>
            <Button
                v-if="!viewport.is.mobile"
                variant="create"
                size="m" heading
                data-placement="bottom"
                :status="isDuplicating ? 'waiting' : null"
                @click="cta('Give it a go', 'bottom')"
            >
                Give it a go
            </Button>
            <Button
                v-else
                variant="create"
                size="m" heading
                :href="content.data.templateUrl"
                target="_blank"
                @click="$track.event('Template Previewed', {
                    'button_placement': 'bottom',
                    'button_cta': 'Preview now'
                })"
            >
                Preview now
            </Button>
        </section>
    </div>
    <div
        class="related-templates section-wrapper"
        :class="viewport.is"
    >
        <section>
            <div class="related-grid">
                <div class="related-grid-left">
                    <GradientText :size="viewport.is.mobile ? 'l' : 'xl'" heading>
                        Check out more<br />
                        templates
                    </GradientText>
                    <Txt size="l">
                        Our L&D experts have created over 30 prebuilt templates.
                    </Txt>
                    <slot name="left" />
                </div>
                <div class="related-grid-right">
                    <!-- <Button
                        variant="create"
                        size="s" heading
                        :href="/templates"
                    >
                        All templates
                    </Button> -->
                    <ul>
                        <li v-for="c, categoryIndex in categories" :key="categoryIndex">
                            <Icon name="correct" right />
                            <Txt>
                                {{ c }}
                            </Txt>
                        </li>
                    </ul>
                    <slot name="right" />
                </div>
            </div>
            <div class="template-grid">
                <a
                    v-for="related, relatedIndex in relatedTemplates"
                    :key="relatedIndex"
                    :href="`/templates/${related.slug}`"
                >
                    <NuxtImg
                        :src="related.thumbnail"
                        :alt="related.title"
                        provider="builderio"
                        :modifiers="{ auto: 'compress,format', q: '90', dpr: '2' }"
                        width="2000"
                        height="1000"
                        sizes="xl:30vw lg:30vw md:30vw sm:30vw xs:30vw"
                        loading="lazy"
                    />
                </a>
            </div>
        </section>
    </div>
    <Accolades />
</template>

<script>
definePageMeta({
    layout: 'cms',
    model: 'course-template'
});

import { useResponsive } from '@/store/responsive';
import { fetchEntries, Content } from '@builder.io/sdk-vue';

import Txt from '@/components/form/txt';
import Icon from '@/components/utility/icon';
import Chip from '@/components/utility/chip';
import Button from '@/components/utility/button';
import Accolades from '@/components/author/cms/accolades';
import GradientText from '@/components/animation/gradient-text';

export default {
    name: 'CourseTemplateCatchAllRoute',
    components: { Content, Txt, Icon, Chip, Button, Accolades, GradientText },
    inject: ['$global', 'apiKey', 'customComponents', 'content'],
    data() {
        return {
            template: null,
            relatedTemplates: null,
            features: [
                'Interactive content',
                'Editable content & themes',
                'Built for all devices',
                'Brand / White label*',
                'Export to SCORM*',
                'Unlimited sharing / views',
                'Get insights and feedback'
            ],
            title: null,
            category: null,
            categories: [
                'Policies & procedures',
                'Customer training',
                'Team training'
            ],
            isDuplicating: false,
            viewport: { is: {} }
        };
    },
    computed: {
        slug() {
            return this.$route.path.replace('/templates/', '');
        },
    },
    mounted() {
        this.getRelatedTemplates();
        this.viewport = useResponsive();
    },
    methods: {
        getRelatedTemplates() {
            fetchEntries({
                model: 'course-template',
                apiKey: this.apiKey,
                options: { omit: 'data.blocks' }
                
            }).then(response => {
                this.relatedTemplates = response
                    .filter(template => template.data.slug !== this.slug && template.data.thumbnail && template.data.category.value && template.data.category.value.data.title === this.category)
                    .map(template => template.data)
                    .slice(0, 3);
            });
        },
        getTemplateCourse(data) {
            this.title = data.title;
            this.category = data.category && data.category.value && data.category.value.data.title;
            this.$track.baseProperties = {
                'page_name': this.category,
                'page_type': 'template landing page',
                'title': this.title
            };
            if (!data.templateId) return;
            return this.$http.request({
                method: 'GET',
                url: `/course/template/${data.templateId}`,
                baseURL: this.$global.isProduction ? this.$http.baseUrl : 'https://api.coassemble.com'
            }).then(({ data }) => this.template = data);
        },
        cta(cta, placement) {
            if (this.$global.isLoggedIn) {
                if (!this.template) return;
                this.isDuplicating = true;
                return this.$http.post(`/course/template/${this.template.id}`)
                    .then(({ data }) => {
                        this.$track.event('Course Created', {
                            'course_title': this.template.title,
                            'course_creation_type': 'template',
                            'button_cta': cta,
                            'button_placement': placement,
                            'pre_signup_course': false
                        });

                        this.$router.push(`/course/${data.id}`);
                    });
            }
            if (this.template) localStorage.setItem('coassemble:tempcourse', JSON.stringify(this.template));
            localStorage.setItem('coassemble:template', JSON.stringify({
                'build_method': 'template',
                'template_category': this.category,
                'template_name': this.title
            }));
            window.location.href = '/build';
        },
        isCMSImage(image) {
            return image.includes('cdn.builder.io');
        }
    }
};
</script>

<style lang="less" scoped>
.template-hero.section-wrapper {
    section {
        align-items: flex-start;
        padding-bottom: 48px;
        gap: 24px;
        border-bottom: 1px solid var(--smoke);
        .chip {
            --chip-colour: var(--primary);
            padding: 8px 16px;
            height: auto;
            a { margin-right: 4px; }
        }
    }
    .grid {
        display: grid;
        grid-template-columns: 1.5fr 0.5fr;
        gap: 48px;
        .grid-left {
            .txt-component.heading {
                margin: 0;
            }
        }

        .grid-right {
            display: flex;
            flex-direction: column;
            gap: 24px;
            padding: 24px 0;
            .actions {
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 8px;
                .cobutton {
                    padding: 10px 24px;
                    height: 40px;
                }
                .cobutton.ghost {
                    border-radius: 40px;
                    border-width: 1.5px;
                }
            }
            .txt-component.xs { color: var(--asphalt); }
        }
    }
    &.tablet {
        padding: 40px;
        .grid {
            grid-template-columns: 1fr;
            gap: 24px;
            .grid-right {
                padding: 0;
            }
        }
    }
}
.template-embed.section-wrapper {
    position: relative;
    margin: 0;
    padding-top: 0;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 40px;
    max-width: 1200px;
    margin: auto;
    color: var(--anchor);
    iframe {
        position: relative;
        width: 100%;
        aspect-ratio: 800 / 604;
        border: none;
        border-radius: 24px;
        background-color: var(--cloud);
    }
    .cobutton {
        padding: 12px 24px;
    }
    &.tablet iframe {
        aspect-ratio: 800 / 840;
    }
}
.template-article.section-wrapper {
    overflow: visible;
    padding: 0 40px;
    section {
        flex-direction: row;
        align-items: flex-start;
        gap: 64px;
        article {
            flex: 1;
            padding: 40px;
            display: flex;
            flex-direction: column;
            gap: 56px;
        }
        aside {
            flex: 0 0 336px;
            position: sticky;
            top: 0;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            padding: 40px;
            gap: 24px;
            > .txt-component.heading { color: var(--navy); }
            ul li {
                color: var(--primary);
            }
        }
    }
    &.mobile {
        section article {
            padding: 0;
        }
    }
}
.section-wrapper.template-cta {
    margin: 32px;
    padding: 96px 32px;
    border-radius: 12px;
    section {
        text-align: center;
        gap: 40px;
        .cobutton {
            padding: 18px 88px;
            border-radius: 56px;
        }
    }
    &.mobile {
        padding: 32px;
    }
}

.section-wrapper.related-templates {
    padding: 80px;
    margin: 0 32px;
    border-radius: 12px;
    background-color: var(--cloud);

    .txt-component.heading { display: inline; }
    ul { color: var(--primary); }

    .related-grid {
        width: 100%;
        display: flex;
        padding: 48px 0px;
        gap: 160px;
        align-items: flex-start;
        align-content: center;
        flex-wrap: wrap;
        .related-grid-left {
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 32px;
        }
        .related-grid-right {
            flex: 1 1 auto;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: center;
            gap: 32px;
            max-width: 360px;
            padding: 0 24px;
            margin-top: 64px;
            .cobutton {
                padding: 16px 32px;
                border-radius: 48px;
            }
        }
    }
    .template-grid {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        gap: 16px;
        width: 100%;
        > a {
            aspect-ratio: 310 / 192;
            background-color: var(--smoke);
            border-radius: 16px;
        }
    }
    &.lg {
        .section-grid {
            gap: 32px;
        }
    }
    &.tablet {
        padding: 24px;
        padding-bottom: 0;
        margin: 0;
        border-radius: 0;
        .related-grid {
            flex-direction: column;
            align-items: flex-start;
            align-content: baseline;
            padding: 24px 0;
            gap: 0;
            .related-grid-right {
                padding: 0;
                flex: 0 0 100%;
            }
        }
        .template-grid {
            grid-template-columns: 1fr;
            gap: 24px;
        }
    }
}
</style>

<style lang="less">
.template-hero.section-wrapper {
    .chip .txt-component { font-weight: 700; }
}
.template-article.section-wrapper {
    article .builder-blocks {
        display: flex;
        flex-direction: column;
        gap: 56px;
        ul:not(.accordion) {
            list-style: disc;
            padding-left: 24px;
            margin: 12px 0;
            li { display: list-item; }
        }
    }
}
</style>
